import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from './Modal';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/posthog';
import { useTranslation } from 'react-i18next';

interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
}

export function DeleteAccountModal({ open, onClose }: DeleteAccountModalProps) {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const confirmWord = t('deleteAccount.confirmWord');
  const canDelete = confirmText === confirmWord;

  const handleDelete = async () => {
    if (!canDelete) return;
    setError('');
    setLoading(true);
    try {
      const { data, error: err } = await supabase.functions.invoke('delete-account');
      if (err) {
        // Try to extract the real error from the response
        const detail = data?.error || data?.detail || err.message;
        throw new Error(detail);
      }
      trackEvent('account_deleted');
      await supabase.auth.signOut();
      window.location.reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unknown error');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    setError('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={t('deleteAccount.title')}>
      <div className="flex flex-col gap-4">
        <p className="text-sm font-semibold text-stone-600 leading-relaxed">
          {t('deleteAccount.warning')}
        </p>
        <label className="text-xs font-bold text-stone-400 uppercase tracking-widest">
          {t('deleteAccount.confirmLabel', { word: confirmWord })}
        </label>
        <input
          type="text"
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          placeholder={confirmWord}
          className="w-full px-5 py-4 bg-stone-50 rounded-2xl border border-stone-200 text-sm font-semibold text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500 transition-all"
        />
        {error && <p className="text-sm font-semibold text-rose-500">{error}</p>}
        <button
          onClick={handleDelete}
          disabled={!canDelete || loading}
          className="w-full bg-rose-500 text-white py-4 rounded-2xl font-bold text-sm hover:bg-rose-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('deleteAccount.submit')}
        </button>
      </div>
    </Modal>
  );
}
